import axios, { HttpStatusCode } from 'axios';
import { USER_SEASON_ERROR_MESSAGES } from '../../corelogic/domain/errors/gateway.errorMessages/userSeason.error.messages';
import {
  UserSeasonInvalidDataError,
  UserSeasonServerError,
} from '../../corelogic/domain/errors/userSeason.error';
import {
  SeasonFighterFrequency,
  SeasonUsersTotalPoints,
  UserSeason,
  UserSeasonFighter,
} from '../../corelogic/domain/models/userSeason.model';
import { UserSeasonGateway } from '../../corelogic/domain/ports/userSeasons.port';
import { getSecureRequestHeaders } from '../../corelogic/domain/utils/securityHeaders.utils';
import { handleAuthError } from './auth.error.handler';

export class ApiUserSeasonGateway implements UserSeasonGateway {
  private apiEndpoint: string;

  private handleUserSeasonError(error: unknown): never {
    if (!axios.isAxiosError(error)) {
      throw new UserSeasonServerError(USER_SEASON_ERROR_MESSAGES.SERVER_ERROR);
    }

    // Check if error is auth-related
    if (error.response?.data?.error?.includes('token')) {
      throw handleAuthError(error);
    }

    switch (error.response?.status) {
      case HttpStatusCode.BadRequest:
        const errorMessage = error.response.data.error;
        if (errorMessage.includes('Season ID')) {
          throw new UserSeasonInvalidDataError(
            USER_SEASON_ERROR_MESSAGES.INVALID_SEASON_ID,
          );
        }
        // Default bad request case
        throw new UserSeasonInvalidDataError(
          USER_SEASON_ERROR_MESSAGES.INVALID_DATA,
        );
      case HttpStatusCode.InternalServerError:
        throw new UserSeasonServerError(
          USER_SEASON_ERROR_MESSAGES.SERVER_ERROR,
        );
      default:
        throw new UserSeasonServerError(
          USER_SEASON_ERROR_MESSAGES.SERVER_ERROR,
        );
    }
  }

  constructor(apiEndpoint: string) {
    this.apiEndpoint = apiEndpoint;
  }

  async upsertUserSeason(
    token: string,
    seasonId: string,
    fighters: UserSeasonFighter[],
  ): Promise<UserSeason> {
    try {
      const response = await axios.put<{ userSeason: UserSeason }>(
        `${this.apiEndpoint}/seasons/${seasonId}`,
        { fighters },
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      return response.data.userSeason;
    } catch (error) {
      throw this.handleUserSeasonError(error);
    }
  }

  async retrieveUserSeason(
    token: string,
    seasonId: string,
  ): Promise<UserSeason> {
    try {
      const response = await axios.get<{ userSeason: UserSeason }>(
        `${this.apiEndpoint}/seasons/${seasonId}`,
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      return response.data.userSeason;
    } catch (error) {
      throw this.handleUserSeasonError(error);
    }
  }

  async retrieveUserSeasons(
    token: string,
    seasonIds: string[],
  ): Promise<UserSeason[]> {
    try {
      const response = await axios.get<{ userSeasons: UserSeason[] }>(
        `${this.apiEndpoint}/seasons`,
        {
          params: { seasonIds },
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      console.log(
        'ApiUserSeasonGateway: retrieveUserSeasons',
        response.data.userSeasons,
      );
      return response.data.userSeasons;
    } catch (error) {
      throw this.handleUserSeasonError(error);
    }
  }

  async retrieveFighterFrequencyBySeason(
    token: string,
    seasonId: string,
  ): Promise<SeasonFighterFrequency> {
    try {
      const response = await axios.get<{
        seasonFighterFrequency: SeasonFighterFrequency;
      }>(`${this.apiEndpoint}/seasons/${seasonId}/fighter-frequency`, {
        headers: {
          ...getSecureRequestHeaders(token),
        },
      });
      return response.data.seasonFighterFrequency;
    } catch (error) {
      throw this.handleUserSeasonError(error);
    }
  }

  async retrieveUsersTotalPointsBySeason(
    token: string,
    seasonId: string,
    limit: number,
  ): Promise<SeasonUsersTotalPoints> {
    try {
      const response = await axios.get<{
        seasonUsersTotalPoints: SeasonUsersTotalPoints;
      }>(`${this.apiEndpoint}/seasons/${seasonId}/total-points`, {
        headers: {
          ...getSecureRequestHeaders(token),
        },
        params: { limit },
      });
      return response.data.seasonUsersTotalPoints;
    } catch (error) {
      throw this.handleUserSeasonError(error);
    }
  }
}
